/**
 * pnlUtils.js — single source of truth for position P/L math.
 * ──────────────────────────────────────────────────────────────
 * Both the on-chart position tags (FootprintChartCanvas) and the
 * WalletHoldings panel must show the SAME number for the same position.
 * Previously each computed P/L with its own inline formula and they
 * drifted: the tag valued the whole SOL delta at TODAY's SOL price,
 * while WalletHoldings valued the entry leg at the ENTRY-time SOL price.
 * Whenever SOL moved between entry and now, the two disagreed.
 *
 * These helpers encode the tag's convention (the one users trust):
 * compute the move in SOL terms, then convert at the current SOL price.
 * That yields pure token performance, independent of SOL/USD drift.
 * ──────────────────────────────────────────────────────────────
 */

const DEFAULT_PUMP_SUPPLY = 1_000_000_000

/**
 * Solana spot position P/L. Matches FootprintChartCanvas position-tag math:
 *   entryPriceSol   = entryMcap   / supply
 *   currentPriceSol = currentMcap / supply
 *   pnlSol          = (currentPriceSol - entryPriceSol) * tokenAmount
 *   pnlUsd          = pnlSol * solPriceUsd        (current SOL price)
 *
 * @returns {{ pnlUsd: number|null, pnlPct: number|null, currentValueUsd: number }}
 *   pnlUsd / pnlPct are null when inputs are insufficient (no entry, no
 *   price yet) so callers can choose a fallback or hide the row.
 */
export function solPositionPnl({ entryMcap, currentMcap, tokenAmount, tokenSupply, solPriceUsd }) {
  const supply = tokenSupply > 0 ? tokenSupply : DEFAULT_PUMP_SUPPLY
  const tokens = tokenAmount || 0
  if (!(currentMcap > 0) || tokens <= 0) {
    return { pnlUsd: null, pnlPct: null, currentValueUsd: 0 }
  }
  const currentPriceSol = currentMcap / supply
  const currentValueUsd = solPriceUsd > 0 ? currentPriceSol * tokens * solPriceUsd : 0

  if (!(entryMcap > 0)) {
    // We can value the holding but have no entry to compute P/L against
    return { pnlUsd: null, pnlPct: null, currentValueUsd }
  }
  const entryPriceSol = entryMcap / supply
  const pnlSol = (currentPriceSol - entryPriceSol) * tokens
  const pnlUsd = solPriceUsd > 0 ? pnlSol * solPriceUsd : null
  const pnlPct = entryPriceSol > 0
    ? ((currentPriceSol - entryPriceSol) / entryPriceSol) * 100
    : null
  return { pnlUsd, pnlPct, currentValueUsd }
}

/**
 * Futures position P/L. Matches the tag's futures branch:
 *   long:  (currentPx - entryPx) * qty
 *   short: (entryPx - currentPx) * qty
 *
 * @param fallbackUsd exchange-reported unrealizedPNL, used when local
 *   inputs are insufficient (no live price yet, etc).
 * @returns {{ pnlUsd: number|null, pnlPct: number|null }}
 */
export function futuresPositionPnl({ entryPx, currentPx, qty, side, fallbackUsd } = {}) {
  const e = parseFloat(entryPx || 0)
  const q = parseFloat(qty || 0)
  const c = parseFloat(currentPx || 0)
  if (e > 0 && q > 0 && c > 0) {
    const isLong = side === 'LONG' || side === 'long'
    const move = isLong ? (c - e) : (e - c)
    return { pnlUsd: move * q, pnlPct: (move / e) * 100 }
  }
  return {
    pnlUsd: fallbackUsd != null ? parseFloat(fallbackUsd) : null,
    pnlPct: null,
  }
}